import type { WindowStatus } from '../audit/windows';
import { formatDay } from '../ui/dates';

/**
 * "Has the DM been reminded?" (owner ruling 2026-10-22, round 3 — the
 * Executive asked it about a store 17 days overdue). Flow 1 already records
 * it on each StoreAuditStatus clock: LastNotifiedStatus = the last status an
 * email went out for (due_soon | overdue | escalated | current) and
 * LastOverdueReminderAt = the last overdue reminder (docs/flows.md Flow 1).
 * Shown only while the clock is due soon or overdue — on a current clock
 * that bookkeeping is old news. Escalations go ABOVE the DM; the contact's
 * name isn't shown (Districts is admin-only). "Reminded" means Flow 1 SENT
 * or TRIED to send (it stamps after a failed send too, so a failure is
 * never re-sent hourly). Pure.
 */
export function reminderStatusText(
  status: WindowStatus,
  lastNotifiedStatus: string,
  lastOverdueReminderAt: string | null,
  /** The clock's due date: a reminder stamped BEFORE it belongs to an earlier cycle (Flow 1 never clears the stamp — QA 2026-10-22). */
  nextDueDate: string | null = null,
): string | null {
  if (status !== 'due_soon' && status !== 'overdue') return null;
  const notified = lastNotifiedStatus.trim().toLowerCase();
  const at = lastOverdueReminderAt ? Date.parse(lastOverdueReminderAt) : NaN;
  const due = nextDueDate ? Date.parse(nextDueDate) : NaN;
  const thisCycle = Number.isFinite(at) && !(Number.isFinite(due) && at < due);
  const reminded = thisCycle ? formatDay(lastOverdueReminderAt) : '';
  // Due-soon mail can be switched off per template: silence is not "not sent yet".
  if (status === 'due_soon') return notified === 'due_soon' ? 'Due-soon notice sent' : null;
  if (notified === 'escalated') return reminded ? `Escalated · last reminder ${reminded}` : 'Escalated';
  if (reminded) return `Reminded ${reminded}`;
  if (notified === 'overdue') return 'Overdue notice sent';
  return 'No reminder recorded yet';
}
